import { useState, useRef, useCallback, useMemo } from "react";
import CodeBlock from "./CodeBlock";

export const sanitizeTitle = (title, url, index) => {
  if (title) {
    const cleaned = title.replace(/[^\x20-\x7E\u00C0-\u024F]/g, "").trim();
    const alphaCount = [...cleaned].filter((c) =>
      /[a-zA-Z\u00C0-\u024F]/.test(c),
    ).length;
    if (cleaned.length >= 3 && alphaCount >= cleaned.length * 0.3) {
      return cleaned.length > 25 ? cleaned.slice(0, 24) + "…" : cleaned;
    }
  }
  if (url) {
    try {
      const domain = new URL(url).hostname.replace(/^www\./, "");
      if (domain)
        return domain.length > 25 ? domain.slice(0, 24) + "…" : domain;
    } catch {
      /* ignore */
    }
  }
  return `Source ${index}`;
};

const CitationButton = ({
  index,
  citation,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  onSourceClick,
  activeSourceId,
  citationInteractions,
}) => {
  const handleClick = () => {
    onSourceClick?.(citation);
  };

  const label = sanitizeTitle(citation.title, citation.url, index);
  const isHighlighted = hoveredRef?.current === index;
  const isActive = activeSourceId && activeSourceId === citation.doc_id;
  const interactions = citationInteractions?.[citation.doc_id];

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => onHoverIn(index)}
      onMouseLeave={onHoverOut}
      title={citation.title || `Source ${index}`}
      className={[
        "inline-flex items-center justify-center gap-0.5 h-[18px] px-1.5 mx-[1px] rounded text-[10px] font-light leading-none cursor-pointer transition-all duration-150 align-middle whitespace-nowrap",
        isActive || isHighlighted
          ? "bg-velBlue/25 text-velBlue ring-1 ring-velBlue"
          : "bg-velBlue/10 text-velBlue hover:bg-velBlue/20",
      ].join(" ")}
    >
      {label}
      {interactions &&
        (interactions.like || interactions.dislike || interactions.catalog) && (
          <span className="inline-flex items-center gap-px ml-0.5">
            {interactions.like && (
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-activeCase" />
            )}
            {interactions.dislike && (
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-logoutColor" />
            )}
            {interactions.catalog && (
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-sunColor" />
            )}
          </span>
        )}
    </button>
  );
};

const CITE_PLACEHOLDER_RE = /@@CITE:(\d+)@@/g;

export const escapeCitationsForMarkdown = (text) =>
  text.replace(/\[(\d+)\]/g, "@@CITE:$1@@");

export const injectCitationButtons = (
  children,
  citationMap,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  onSourceClick,
  activeSourceId,
  citationInteractions,
) => {
  if (!citationMap || Object.keys(citationMap).length === 0) return children;

  const process = (child) => {
    if (typeof child !== "string") return child;
    if (!CITE_PLACEHOLDER_RE.test(child)) return child;
    CITE_PLACEHOLDER_RE.lastIndex = 0;

    const parts = child.split(CITE_PLACEHOLDER_RE);
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        const idx = parseInt(part, 10);
        const citation = citationMap[idx];
        if (citation) {
          return (
            <CitationButton
              key={`c${i}`}
              index={idx}
              citation={citation}
              hoveredRef={hoveredRef}
              onHoverIn={onHoverIn}
              onHoverOut={onHoverOut}
              onSourceClick={onSourceClick}
              activeSourceId={activeSourceId}
              citationInteractions={citationInteractions}
            />
          );
        }
        return `[${part}]`;
      }
      return part;
    });
  };

  if (Array.isArray(children)) {
    return children.flatMap((child) => process(child));
  }
  return process(children);
};

export const buildMarkdownComponents = (
  citationMap,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  onSourceClick,
  activeSourceId,
  citationInteractions,
  onOpenInEditor,
) => {
  const pc = (children) =>
    injectCitationButtons(
      children,
      citationMap,
      hoveredRef,
      onHoverIn,
      onHoverOut,
      onSourceClick,
      activeSourceId,
      citationInteractions,
    );

  return {
    p: ({ children }) => (
      <p className="mb-3 last:mb-0 leading-relaxed">{pc(children)}</p>
    ),
    h1: ({ children }) => (
      <h1 className="text-lg font-light mb-2 mt-4 first:mt-0 text-mainText">
        {pc(children)}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-base font-light mb-2 mt-3 first:mt-0 text-mainText">
        {pc(children)}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-sm font-light mb-1.5 mt-2.5 first:mt-0 text-mainText">
        {pc(children)}
      </h3>
    ),
    strong: ({ children }) => (
      <strong className="font-normal text-mainText">{pc(children)}</strong>
    ),
    em: ({ children }) => <em>{pc(children)}</em>,
    ul: ({ children }) => (
      <ul className="mb-3 ml-4 list-disc space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-3 ml-4 list-decimal space-y-1">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{pc(children)}</li>,
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-velBlue underline hover:text-velBlue/80"
      >
        {pc(children)}
      </a>
    ),
    code: ({ inline, className, children }) => {
      if (inline) {
        return (
          <code className="px-1.5 py-0.5 rounded bg-surface/80 border border-inputBorder text-[13px] font-mono text-mainText">
            {children}
          </code>
        );
      }
      const lang = (className || "").replace("language-", "");
      return (
        <CodeBlock
          language={lang}
          code={String(children).replace(/\n$/, "")}
          onOpenInEditor={onOpenInEditor}
        />
      );
    },
    pre: ({ children }) => <>{children}</>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-surfaceBorder pl-3 my-2 text-mutedText">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-3 border-dropdownDivider" />,
    table: ({ children }) => (
      <div className="my-3 overflow-x-auto">
        <table className="min-w-full text-[13px] font-light border-collapse">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-surface/80">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="px-3 py-1.5 text-left font-light border-b border-surfaceBorder text-mainText">
        {pc(children)}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-3 py-1.5 border-b border-surfaceBorder/50">
        {pc(children)}
      </td>
    ),
  };
};

export default CitationButton;
